import React, {useEffect} from 'react';
import {useConfirmationDialog, ConfirmationDialog} from "./ConfirmationDialog";
import {useNotificationSnackbar, NotificationSnackbar} from "./NotificationSnackbar";
import {errorParser} from "../../utils/errors/errorParser";
import axios from "axios";
import {
    BadRequestError, ForbiddenError,
    InternalServerError,
    NotFoundError,
    ValidationError
} from "../../utils/errors/apiError";
import {useTranslation} from "react-i18next";


const FeedbackContext = React.createContext();

const FeedbackProvider = props => {
    const {children} = props;
    const {t} = useTranslation(['components', 'errors']);
    const confirmationDialog = useConfirmationDialog();
    const notificationSnackbar = useNotificationSnackbar(errorParser, t);

    const context = {
        showErrorNotification: notificationSnackbar.showError,
        showSuccessNotification: notificationSnackbar.showSuccess,
        askConfirmation: confirmationDialog.askConfirmation,
    };

    // Show connection error when happen
    useEffect(() => {
        const interceptor = axios.interceptors.response.use(
            (response) => response,
            (error) => {
                const config = error.config || {};

                if (!config.skipGlobalErrorHandler) {
                    // Check for custom flag
                    let e;
                    const {status, data} = error.response;
                    // set the error according to the status code
                    switch (status) {
                        case 401:
                            break;  // handled in AuthProvider
                        case 403:
                            e = new ForbiddenError(data, t('errors:forbiddenError'), t('feedbackProvider.forbiddenErrorMessage'));  break;
                        case 404:
                            e = new NotFoundError(data).translate(t);  break;
                        case 422:
                            e = new ValidationError(data).translate(t); break;
                        case 400:
                            e = new BadRequestError(data).translate(t); break;
                        case 500:
                            e = new InternalServerError(data).translate(t); break;
                    }
                    if(e !== undefined){
                        notificationSnackbar.showError(e);
                        return Promise.reject(e);
                    }
                }

                return Promise.reject(error);
            }
        );

        return () => {
            // Eject the interceptor when the component unmounts
            axios.interceptors.response.eject(interceptor);
        };
    }, []);

    return (
        <FeedbackContext.Provider value={context} {...props} >
            {children}
            {
                confirmationDialog.isOpen &&
                <ConfirmationDialog
                    options={confirmationDialog.options}
                    onClose={confirmationDialog.onClose}/>
            }
            {
                notificationSnackbar.notification &&
                <NotificationSnackbar
                    notification={notificationSnackbar.notification}
                    onClose={notificationSnackbar.handleClose}/>
            }
        </FeedbackContext.Provider>
    )
};

const useFeedbackTools = () => {
    return React.useContext(FeedbackContext);
};

export {FeedbackProvider, useFeedbackTools}